<template>
  <div class="sales-dashboard" :class="{ 'sales-dashboard--dark': isDarkSkin }">

    <div class="sales-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"
                   @reset="onFilterReset"/>
      <div class="sales-dashboard__toolbar-row">
        <!-- One-click department shortcuts: fills the business-unit filter
             with every unit of that department (e.g. all Retention units). -->
        <div class="dept-quick" v-if="availableDepartments.length">
          <span class="dept-quick__label">{{ $t('filters.departments') }}</span>
          <button v-for="dept in availableDepartments" :key="dept" type="button"
                  class="dept-quick__pill" :class="['dept-quick__pill--' + dept.toLowerCase(), { 'is-active': isDeptActive(dept) }]"
                  @click="toggleDept(dept)">
            <i :class="isDeptActive(dept) ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
            {{ $t(`businessUnitDepartments.${dept}`) }}
            <span class="dept-quick__count">{{ deptBuIds(dept).length }}</span>
          </button>
        </div>
        <calendar-bar ref="calendarBar" v-model="dateRange" @input="scheduleRefresh"/>
        <b-button v-if="hasPermission(permissions.EXPORT_EXCEL)" variant="success" class="sales-dashboard__export-btn"
                  @click="openReportExport">
          <i class="fa-regular fa-file-excel mr-50"></i>{{ $t('salesDashboard.exportReport') }}
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <b-row class="kpi-row">
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <!-- FTD card → FTD deposits behind the number -->
          <div class="kpi-card kpi-card--primary kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')"
               @click="openKpiTx($t('FTD / Total Clients'), { isFtd: true, excludeRefundedFtdClients: true })">
            <div class="kpi-card__icon">
              <feather-icon icon="UsersIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD / Total Clients') }}</div>
              <div class="kpi-card__value kpi-card__value--triple">
                <span class="kpi-trip">
                  <span class="kpi-trip__num">{{ statistics.ftdsCount }}</span>
                  <span class="kpi-trip__label">{{ $t('salesDashboard.ftd') }}</span>
                </span>
                <span class="kpi-card__value-sep">/</span>
                <span class="kpi-trip kpi-trip--dup" role="button" tabindex="0"
                      v-b-tooltip.hover :title="$t('salesDashboard.viewDuplicated')"
                      @click.stop="openDrill('DUPLICATED', { value: 'DUPLICATED', label: $t('columns.duplicated') })">
                  <span class="kpi-trip__num">{{ statistics.duplicatedCount }}</span>
                  <span class="kpi-trip__label">{{ $t('columns.duplicated') }}</span>
                </span>
                <span class="kpi-card__value-sep">/</span>
                <span class="kpi-trip">
                  <span class="kpi-trip__num">{{ statistics.clientsCount }}</span>
                  <span class="kpi-trip__label">{{ $t('salesDashboard.total') }}</span>
                </span>
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="WifiIcon" size="12" class="mr-25"/>
                {{ $t('Online') }}: {{ statistics.onlineClients }}
              </div>
            </div>
            <button type="button" class="kpi-card__newtab" v-b-tooltip.hover
                    :title="$t('salesDashboard.openInNewTab')"
                    @click.stop="openKpiNewTab($t('FTD / Total Clients'), { isFtd: true, excludeRefundedFtdClients: true })">
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </button>
            <i class="fa-regular fa-circle-question kpi-card__info" @click.stop
               v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.ftdCard')"></i>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')"
               @click="openKpiTx($t('Total Deposits'))">
            <div class="kpi-card__icon">
              <i class="fa-solid fa-coins" style="font-size: 22px;"></i>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalDeposits) }}</div>
              <!-- Sales (FTD deposits) vs Retention (all other deposits) split -->
              <div class="kpi-card__split" v-b-tooltip.hover :title="$t('salesDashboard.depositSplitHint')">
                <span class="kpi-card__split-chip">
                  {{ $t('salesDashboard.salesShare') }} {{ convertToEURO(statistics.salesDeposits) }}
                </span>
                <span class="kpi-card__split-chip">
                  {{ $t('salesDashboard.retentionShare') }} {{ convertToEURO(statistics.retentionDeposits) }}
                </span>
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="TrendingUpIcon" size="12" class="mr-25"/>
                {{ $t('Net') }}: {{ convertToEURO(statistics.netDeposits) }}
              </div>
            </div>
            <button type="button" class="kpi-card__newtab" v-b-tooltip.hover
                    :title="$t('salesDashboard.openInNewTab')"
                    @click.stop="openKpiNewTab($t('Total Deposits'))">
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </button>
            <i class="fa-regular fa-circle-question kpi-card__info" @click.stop
               v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.deposits')"></i>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--danger kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')"
               @click="openKpiTx($t('Withdrawals'), { type: 'WITHDRAWAL' })">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="PercentIcon" size="12" class="mr-25"/>
                {{ $t('Rate') }}: {{ withdrawalRate }}%
              </div>
            </div>
            <button type="button" class="kpi-card__newtab" v-b-tooltip.hover
                    :title="$t('salesDashboard.openInNewTab')"
                    @click.stop="openKpiNewTab($t('Withdrawals'), { type: 'WITHDRAWAL' })">
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </button>
            <i class="fa-regular fa-circle-question kpi-card__info" @click.stop
               v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.withdrawals')"></i>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <!-- TXF (taxes): always shown. Its money folds INTO Total Deposits /
             Net unless the "Exclude TXF" filter is ticked (never into the
             agent tables or the Sales/Retention chips). -->
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--txf kpi-card--click" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.txfHint')"
               @click="openTxfTx">
            <div class="kpi-card__icon">
              <i class="fa-solid fa-file-invoice-dollar" style="font-size: 22px;"></i>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.txfAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.txfCount }}
              </div>
            </div>
            <i class="fa-regular fa-circle-question kpi-card__info" @click.stop
               v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.txf')"></i>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <!-- FAKE money (approved transactions marked realType FAKE): a slim
           strip of mini-pills — deliberately much smaller than the real KPI
           cards so fake money reads as a footnote, never as real money. Net
           (which already reflects fake withdrawals) rides along in the
           deposits pill to keep the strip lean. -->
      <div class="fake-strip">
        <!-- The FAKE badge doubles as the switch: off = fake pills AND the
             fake pills/amounts inside the agent tables are hidden -->
        <button type="button" class="fake-strip__badge" :class="{ 'is-off': !showFake }"
                v-b-tooltip.hover :title="$t('salesDashboard.fakeToggleHint')" @click="toggleFake">
          <span class="fake-strip__track"><span class="fake-strip__thumb"></span></span>
          FAKE
        </button>
        <div v-if="showFake" class="fake-pill fake-pill--click" role="button" tabindex="0"
             v-b-tooltip.hover :title="$t('salesDashboard.viewTransactions')"
             @click="openKpiTx($t('Fake Deposits'), { realType: ['FAKE'] })">
          <i class="fa-solid fa-coins" style="font-size: 12px;"></i>
          <span class="fake-pill__label">{{ $t('Fake Deposits') }}</span>
          <span class="fake-pill__value">{{ convertToEURO(statistics.fakeDeposits) }}</span>
          <span class="fake-pill__count">({{ statistics.fakeDepositsCount }})</span>
          <span class="fake-pill__count">{{ $t('Net') }}: {{ convertToEURO(statistics.fakeNetDeposits) }}</span>
        </div>
      </div>

      <!-- Sales performance strip -->
      <b-row class="perf-row">
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--blue"><feather-icon icon="TargetIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ conversionRate }}%</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.conversionRate') }}
                <i class="fa-regular fa-circle-question perf-info" v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.conversion')"></i>
              </div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--green"><i class="fa-solid fa-coins" style="font-size: 18px;"></i></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ convertToEURO(avgDeposit) }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.avgDeposit') }}
                <i class="fa-regular fa-circle-question perf-info" v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.avgDeposit')"></i>
              </div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--purple"><feather-icon icon="UsersIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ activeAgents }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.activeAgents') }}
                <i class="fa-regular fa-circle-question perf-info" v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.activeAgents')"></i>
              </div>
            </div>
          </div>
        </b-col>
        <b-col md="3" sm="6" class="mb-2">
          <div class="perf-chip">
            <div class="perf-chip__icon perf-chip__icon--amber"><feather-icon icon="HashIcon" size="20"/></div>
            <div class="perf-chip__body">
              <div class="perf-chip__value">{{ depositTxCount }}</div>
              <div class="perf-chip__label">{{ $t('salesDashboard.depositsCount') }}
                <i class="fa-regular fa-circle-question perf-info" v-b-tooltip.hover.bottom :title="$t('salesDashboard.kpiInfo.depositsCount')"></i>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Sales agents + Retention agents leaderboards -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="PhoneCallIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('salesDashboard.salesAgents') }}</h5>
              </div>
              <div class="d-flex align-items-center">
                <span class="panel-card__hint sales-total">
                  <span class="sales-total__received" role="button" tabindex="0"
                        v-b-tooltip.hover :title="$t('salesDashboard.receivedLeadsTooltip')"
                        @click="openTeamAssignments('SALES')"
                        @keydown.enter.prevent="openTeamAssignments('SALES')">
                    <i class="fa-solid fa-user-plus mr-25"></i>{{ salesTotals.leads }} {{ $t('salesDashboard.received') }}
                  </span>
                  <span class="mx-50">·</span>
                  <span class="sales-total__ftd" :class="{ 'sd-masked': hideAmounts }" v-b-tooltip.hover :title="$t('salesDashboard.ftd')">
                    {{ salesTotals.ftd }} {{ $t('salesDashboard.ftd') }} · {{ convertToEURO(salesTotals.amount) }}
                  </span>
                  <!-- withdrawals processed by SALES agents (their retention-table
                       counterpart row is hidden, so this money lives here) -->
                  <template v-if="salesTotals.withdraw">
                    <span class="mx-50">·</span>
                    <span class="sales-total__wd" :class="{ 'sd-masked': hideAmounts }" v-b-tooltip.hover :title="$t('Total Withdrawals')">
                      {{ convertToEURO(salesTotals.withdraw) }}
                    </span>
                  </template>
                </span>
                <button type="button" class="sd-eye" :class="{ 'is-on': hideAmounts }"
                        v-b-tooltip.hover :title="hideAmounts ? $t('salesDashboard.showAmounts') : $t('salesDashboard.hideAmounts')"
                        @click="toggleAmounts">
                  <i :class="hideAmounts ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
              </div>
            </div>
            <div class="panel-card__body">
              <top-agents-leaderboard :agents="statistics.salesAgentBreakdown" metric="ftd"
                                      :loading="loading" :is-dark-skin="isDarkSkin" :show-fake="showFake"
                                      :hide-amounts="hideAmounts"
                                      @select="openSalesman($event)"
                                      @leads-click="openAssignments($event)"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="AwardIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('salesDashboard.retentionAgents') }}</h5>
              </div>
              <div class="d-flex align-items-center">
                <span class="panel-card__hint sales-total">
                  <!-- clients handed to retention agents in the period (row receivedCount sum) -->
                  <span class="sales-total__received" role="button" tabindex="0"
                        v-b-tooltip.hover :title="$t('salesDashboard.receivedClientsTooltip')"
                        @click="openTeamAssignments('RETENTION')"
                        @keydown.enter.prevent="openTeamAssignments('RETENTION')">
                    <i class="fa-solid fa-user-plus mr-25"></i>{{ retentionTotals.received }} {{ $t('salesDashboard.received') }}
                  </span>
                  <span class="mx-50">·</span>
                  <!-- real deposits / withdrawals: same count-·-amount pill layout as the FAKE pill -->
                  <span class="sales-total__real" :class="{ 'sd-masked': hideAmounts }" v-b-tooltip.hover :title="$t('salesDashboard.depositsCount')">
                    {{ retentionTotals.count }} · {{ convertToEURO(retentionTotals.deposit) }}
                  </span>
                  <span class="mx-50">·</span>
                  <span class="sales-total__wd" :class="{ 'sd-masked': hideAmounts }" v-b-tooltip.hover :title="$t('Total Withdrawals')">
                    {{ convertToEURO(retentionTotals.withdraw) }}
                  </span>
                  <!-- retention money with no table row → the gap to the KPI split -->
                  <template v-if="retentionOther">
                    <span class="mx-50">·</span>
                    <span class="sales-total__other" :class="{ 'sd-masked': hideAmounts }" role="button" tabindex="0"
                          v-b-tooltip.hover :title="$t('salesDashboard.retentionOtherHint')"
                          @click="openRetentionOther">
                      +{{ convertToEURO(retentionOther) }}
                    </span>
                  </template>
                  <!-- total FAKE (non-FTD) deposits across the table, same source as the row lines -->
                  <template v-if="showFake">
                    <span class="mx-50">·</span>
                    <span class="sales-total__fake" :class="{ 'sd-masked': hideAmounts }" v-b-tooltip.hover :title="$t('Fake Deposits')">
                      {{ retentionTotals.fakeCount }} FAKE · {{ convertToEURO(retentionTotals.fakeAmount) }}
                    </span>
                  </template>
                </span>
                <!-- Managers/admins rows switch (default OFF) -->
                <button type="button" class="sd-mgr-toggle" :class="{ 'is-on': showManagers }"
                        v-b-tooltip.hover :title="$t('salesDashboard.includeManagersHint')"
                        @click="toggleManagers">
                  <i class="fa-solid fa-user-tie"></i>
                </button>
                <button type="button" class="sd-eye" :class="{ 'is-on': hideAmounts }"
                        v-b-tooltip.hover :title="hideAmounts ? $t('salesDashboard.showAmounts') : $t('salesDashboard.hideAmounts')"
                        @click="toggleAmounts">
                  <i :class="hideAmounts ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
              </div>
            </div>
            <div class="panel-card__body">
              <top-agents-leaderboard :agents="retentionRows" :loading="loading"
                                      :is-dark-skin="isDarkSkin" :show-fake="showFake"
                                      :hide-amounts="hideAmounts"
                                      @select="openDrill('AGENT', $event)"
                                      @leads-click="openAssignments($event)"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Deposits by Country + Clients by Status -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip"><feather-icon icon="GlobeIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Deposits by Country') }}</h5>
              </div>
              <div class="d-flex align-items-center">
                <div class="panel-card__toggle">
                  <span :class="{'text-muted': showCountryDeposit}">€</span>
                  <b-form-checkbox switch v-model="showCountryDeposit" class="mx-50"/>
                  <span :class="{'text-muted': !showCountryDeposit}">#</span>
                </div>
                <b-button v-if="hasPermission(permissions.EXPORT_EXCEL)" size="sm" variant="flat-success" class="ml-50"
                          v-b-tooltip.hover :title="$t('operations.export')" @click="exportDepositsByCountry">
                  <i class="fa-regular fa-file-excel"></i>
                </b-button>
              </div>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="depositCountryItems" :currency="!showCountryDeposit"
                               :loading="loading" :is-dark-skin="isDarkSkin"
                               @select="openDrill('DEPOSIT_COUNTRY', $event)"/>
            </div>
          </div>
        </b-col>

        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt"><feather-icon icon="PieChartIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Status') }}</h5>
              </div>
              <div class="d-flex align-items-center">
                <span class="panel-card__hint"><feather-icon icon="MousePointerIcon" size="12"/> {{ $t('salesDashboard.clickToDrill') }}</span>
                <b-button v-if="hasPermission(permissions.EXPORT_EXCEL)" size="sm" variant="flat-success" class="ml-50"
                          v-b-tooltip.hover :title="$t('operations.export')" @click="exportClientsByStatus">
                  <i class="fa-regular fa-file-excel"></i>
                </b-button>
              </div>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="statusItems" :loading="loading"
                               :is-dark-skin="isDarkSkin" @select="openDrill('CLIENT_STATUS', $event)"/>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Clients by Country -->
      <b-row class="mt-1">
        <b-col lg="6" sm="12" class="mb-2">
          <div class="panel-card">
            <div class="panel-card__header">
              <div class="panel-card__title-wrap">
                <div class="panel-card__icon-chip panel-card__icon-chip--alt"><feather-icon icon="MapPinIcon" size="18"/></div>
                <h5 class="panel-card__title mb-0">{{ $t('Clients by Country') }}</h5>
              </div>
              <div class="d-flex align-items-center">
                <span class="panel-card__hint"><feather-icon icon="MousePointerIcon" size="12"/> {{ $t('salesDashboard.clickToDrill') }}</span>
                <b-button v-if="hasPermission(permissions.EXPORT_EXCEL)" size="sm" variant="flat-success" class="ml-50"
                          v-b-tooltip.hover :title="$t('operations.export')" @click="exportClientsByCountry">
                  <i class="fa-regular fa-file-excel"></i>
                </b-button>
              </div>
            </div>
            <div class="panel-card__body">
              <ranked-bar-list :items="clientCountryItems" :loading="loading"
                               :is-dark-skin="isDarkSkin" @select="openDrill('CLIENT_COUNTRY', $event)"/>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-overlay>

    <sales-drill-modal ref="drill"/>
    <salesman-modal ref="salesman"/>
    <sales-report-export-modal ref="reportModal"/>
    <insight-transactions-modal ref="txModal"/>
    <assignment-list-modal ref="assignments"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import DashboardSearchRequest from '@/model/Dashboard/DashboardSearchRequest';
import FilterCard from '@/components/widget/FilterCard';
import SalesDashboardFilterFields from '@/model/filter/SalesDashboardFilterFields';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import TopAgentsLeaderboard from '@/components/dashboard/TopAgentsLeaderboard.vue';
import RankedBarList from '@/components/dashboard/RankedBarList.vue';
import SalesDrillModal from '@/components/modal/SalesDrillModal.vue';
import SalesmanModal from '@/components/modal/SalesmanModal.vue';
import SalesReportExportModal from '@/components/modal/SalesReportExportModal.vue';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal.vue';
import AssignmentListModal from '@/components/modal/AssignmentListModal.vue';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'SalesDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    TopAgentsLeaderboard,
    RankedBarList,
    SalesDrillModal,
    SalesmanModal,
    SalesReportExportModal,
    InsightTransactionsModal,
    AssignmentListModal,
    CalendarBar,
    FilterCard,
  },

  data: () => ({
    filter: DashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: '', until: '' },
    // FAKE switch: shows/hides the fake KPI pills AND the fake pills/amounts
    // inside the agent tables. Remembered across visits.
    showFake: localStorage.getItem('sales-dashboard-show-fake') !== '0',
    // Retention table: include manager/admin rows — ALWAYS starts off.
    showManagers: false,
    // Privacy eye: default OPEN — click blurs the agent-table figures on
    // demand (pure display toggle, nothing persisted).
    hideAmounts: false,
    // Sales-table ranking: 'real' = by real FTDs (default), 'fake' = fake FTDs first.
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    statistics: {
      totalDeposits: 0,
      salesDeposits: 0,
      retentionDeposits: 0,
      netDeposits: 0,
      totalWithdrawals: 0,
      // TXF (taxes) — own KPI, never inside the deposit totals
      txfAmount: 0,
      txfCount: 0,
      onlineClients: 0,
      clientsCount: 0,
      ftdsCount: 0,
      // Clients created in range marked as duplicated (affiliateSource set)
      duplicatedCount: 0,
      // FAKE money (approved transactions marked realType FAKE)
      fakeDeposits: 0,
      fakeDepositsCount: 0,
      fakeWithdrawals: 0,
      fakeWithdrawalsCount: 0,
      fakeNetDeposits: 0,
      userDeposit: {},
      userCount: {},
      agentBreakdown: [],
      salesAgentBreakdown: [],
      countryDeposit: {},
      countryCount: {},
      statusMapCount: {},
      clientCountryMapCount: {},
    },
    showCountryDeposit: false,
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('clients', ['allStatuses']),
    ...mapGetters('data', ['allBusinessUnits']),
    viewKey() {
      return this.$route.name || 'sales-dashboard';
    },
    // Departments that actually have business units (SALES / RETENTION / LANDING).
    availableDepartments() {
      const present = new Set((this.allBusinessUnits || []).map(b => b.type).filter(Boolean));
      return ['SALES', 'RETENTION', 'LANDING'].filter(t => present.has(t));
    },
    countryDepositData() {
      return !this.showCountryDeposit ? this.statistics.countryDeposit : this.statistics.countryCount;
    },
    statusMap() {
      return (this.allStatuses || []).reduce((m, s) => {
        m[s.id] = { name: s.status, color: s.color };
        return m;
      }, {});
    },
    countryMap() {
      const list = this.$t('countryList') || [];
      return (Array.isArray(list) ? list : []).reduce((m, c) => {
        m[c.code] = c.name;
        return m;
      }, {});
    },
    statusItems() {
      const map = this.statistics.statusMapCount || {};
      return Object.keys(map).map(id => {
        const meta = this.statusMap[id] || { name: id, color: '#999999' };
        return { key: id, label: meta.name, color: meta.color, value: map[id] };
      }).sort((a, b) => b.value - a.value);
    },
    clientCountryItems() {
      const map = this.statistics.clientCountryMapCount || {};
      return Object.keys(map).map(code => ({
        key: code,
        label: this.countryMap[code] || code || 'Unknown',
        flag: this.flag(code),
        value: map[code],
      })).sort((a, b) => b.value - a.value);
    },
    depositCountryItems() {
      const map = this.countryDepositData || {};
      return Object.keys(map).map(code => ({
        key: code,
        label: this.countryMap[code] || code || 'Unknown',
        flag: this.flag(code),
        value: Number(map[code]),
      })).sort((a, b) => b.value - a.value);
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    },
    depositTxCount() {
      return (this.statistics.agentBreakdown || []).reduce((s, a) => s + (a.count || 0), 0);
    },
    avgDeposit() {
      const c = this.depositTxCount;
      return c ? (this.statistics.totalDeposits || 0) / c : 0;
    },
    conversionRate() {
      const clients = this.statistics.clientsCount || 0;
      if (!clients) return '0.0';
      return ((this.statistics.ftdsCount || 0) / clients * 100).toFixed(1);
    },
    activeAgents() {
      return (this.statistics.agentBreakdown || []).filter(a => (a.deposit || 0) > 0).length;
    },
    // Totals for the Sales table: leads received (backend `receivedCount`,
    // falling back to the legacy pending-leads counter), FTD count, FTD money.
    // FTD count/money come from the BACKEND headline (ftdsCount/salesDeposits)
    // — summing the visible rows undercounts, because FTDs with no depositor
    // or made by managers/hidden agents have no row yet are real FTDs (the
    // KPI card and the drill list agree on the headline figures).
    salesTotals() {
      const list = this.statistics.salesAgentBreakdown || [];
      const leads = list.reduce((t, a) =>
        t + (a.receivedCount != null ? a.receivedCount : (a.salesLeads || 0)), 0);
      const ftd = this.statistics.ftdsCount || 0;
      const amount = this.statistics.salesDeposits || 0;
      // Sales agents' processed withdrawals — row sum, this table's own money.
      const withdraw = list.reduce((t, a) => t + (a.withdraw || 0), 0);
      return { leads, ftd, amount, withdraw, conv: leads ? (ftd / leads * 100).toFixed(0) : '0' };
    },
    // Totals for the Retention table: what the RETENTION agents themselves
    // processed (pure row sums — each table owns its own numbers; sales
    // agents' withdrawals live in the Sales table, website/client-made
    // withdrawals only in the KPI card).
    // Every retention row that belongs to SOMEBODY (managers included) —
    // the basis for the "+other" gap, independent of the display switch.
    allRetentionRows() {
      return (this.statistics.agentBreakdown || [])
          .filter(a => a.userId && a.name !== 'No User');
    },
    // What the table displays: managers/admins only when the switch is on.
    retentionRows() {
      return this.allRetentionRows
          .filter(a => this.showManagers || (a.role !== 'MANAGER' && a.role !== 'ADMINISTRATOR'));
    },
    retentionTotals() {
      // Sum only the rows the table actually SHOWS — the header must equal
      // what the rows add up to.
      const list = this.retentionRows;
      const count = list.reduce((t, a) => t + (a.count || 0), 0);
      const deposit = list.reduce((t, a) => t + (a.deposit || 0), 0);
      const withdraw = list.reduce((t, a) => t + (a.withdraw || 0), 0);
      const fakeCount = list.reduce((t, a) => t + (a.fakeDepositCount || 0), 0);
      const fakeAmount = list.reduce((t, a) => t + (a.fakeDeposit || 0), 0);
      const received = list.reduce((t, a) => t + (a.receivedCount || 0), 0);
      return { count, deposit, withdraw, fakeCount, fakeAmount, received };
    },
    // Retention money with no DISPLAYED row: unassigned deposits, sales
    // agents' redeposits, refunded clients' ex-FTD money, hidden users — and
    // the managers' money while the switch is OFF. Turning the switch on
    // moves the manager money out of this pill and into the rows/totals.
    retentionOther() {
      const diff = (this.statistics.retentionDeposits || 0) - (this.retentionTotals.deposit || 0);
      return diff > 0.5 ? diff : 0;
    },
    drillCtx() {
      return {
        orgIds: this.filter.orgIds,
        buIds: this.filter.buIds,
        buTypes: this.filter.buTypes,
        affiliateId: this.filter.affiliateId,
        banner: this.filter.banner,
        campaign: this.filter.campaign,
        realOnly: this.filter.realOnly,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
    },
  },
  created() {
    this.filterFields = SalesDashboardFilterFields();
    // Ensure client statuses (names + colors) are available for the status list.
    if (!this.allStatuses || !this.allStatuses.length) {
      this.refreshData();
    }
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getSalesDashboard']),
    ...mapActions('clients', ['refreshData']),
    toggleFake() {
      this.showFake = !this.showFake;
      try { localStorage.setItem('sales-dashboard-show-fake', this.showFake ? '1' : '0'); } catch (e) { /* storage disabled */ }
    },
    toggleManagers() {
      // Purely a display toggle — the rows are already in the response.
      this.showManagers = !this.showManagers;
    },
    toggleAmounts() {
      this.hideAmounts = !this.hideAmounts;
    },
    // Filter reset → the date filter goes to "All" (no dates; the backend
    // falls back to full history). The bar emits the new range → refresh.
    onFilterReset() {
      if (this.$refs.calendarBar) {
        this.$refs.calendarBar.reset('all');
      }
    },
    flag(code) {
      if (!code || code.length !== 2) return '';
      return code.toUpperCase().replace(/./g, ch => String.fromCodePoint(127397 + ch.charCodeAt(0)));
    },
    // ---- Department quick-pick: one click selects ALL units of a department ----
    deptBuIds(dept) {
      let bus = (this.allBusinessUnits || []).filter(b => b.type === dept);
      const orgs = this.filter.orgIds || [];
      if (orgs.length) {
        bus = bus.filter(b => orgs.includes(b.orgId));
      }
      return bus.map(b => b.id);
    },
    // A department reads as "on" when every one of its units is selected.
    isDeptActive(dept) {
      const ids = this.deptBuIds(dept);
      const sel = this.filter.buIds || [];
      return ids.length > 0 && ids.every(id => sel.includes(id));
    },
    toggleDept(dept) {
      const ids = this.deptBuIds(dept);
      if (!ids.length) return;
      const sel = new Set(this.filter.buIds || []);
      if (this.isDeptActive(dept)) {
        ids.forEach(id => sel.delete(id));
      } else {
        ids.forEach(id => sel.add(id));
      }
      this.filter.buIds = [...sel]; // deep watcher -> refresh; BU select shows them
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
        // Always fetch manager/admin rows — the switch only shows/hides them
        // client-side.
        includeManagers: true,
      };
      this.inflight = true;
      this.loading = true;
      this.getSalesDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getSalesDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    openSalesman(payload) {
      if (!payload || !payload.value) return;
      this.$refs.salesman.open({ userId: payload.value, label: payload.label, ctx: this.drillCtx });
    },
    // ---- KPI cards → raw monetary transactions behind the number ----
    // Mirrors the backend sales-dashboard aggregation: approved, real (page has
    // no realOnly toggle → backend defaults to REAL), non-test CREDIT_LINE
    // movements within the page's date range / org / BU scope.
    kpiTxFilters(extra) {
      const filters = {
        type: 'DEPOSIT',
        // TXF folds into Total Deposits unless excluded — the drill-down list
        // must show the same rows. (TXF is never FTD and never FAKE, so the
        // FTD/fake cards passing extra filters are unaffected.)
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        status: ['APPROVED'],
        isTest: false,
        realType: this.filter.realOnly === false ? null : ['REAL'],
        orgIds: (this.filter.orgIds && this.filter.orgIds.length) ? this.filter.orgIds : null,
        businessUnit: (this.filter.buIds && this.filter.buIds.length) ? this.filter.buIds : null,
        ...(extra || {}),
      };
      if (this.dateRange && this.dateRange.from) {
        filters.registrationTime = {
          start: this.$moment(this.dateRange.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until || this.dateRange.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      return filters;
    },
    openKpiTx(title, extra) {
      this.$refs.txModal.show(title, this.kpiTxFilters(extra));
    },
    // "+€X other" pill → the retention money with no DISPLAYED row: the exact
    // dashboard retention bucket (incl. refunded clients' ex-FTDs, resolved
    // server-side) minus everything processed by the agents currently shown —
    // so with the managers switch OFF their transactions appear here, and
    // with it ON they don't ($nin also matches missing depositors).
    openRetentionOther() {
      const rowIds = this.retentionRows.map(a => a.userId);
      const filters = this.kpiTxFilters({
        retentionBucket: true,
        excludeUserIds: rowIds,
        // the retention chip is pure agent money — TXF never folds in here
        action: ['CREDIT_LINE'],
      });
      this.$refs.txModal.show(this.$t('salesDashboard.retentionOtherTitle'), filters);
    },
    // TXF card → the raw TAXES transactions behind the number. Dedicated
    // filters (NOT kpiTxFilters — that one forces CREDIT_LINE deposits).
    openTxfTx() {
      const filters = {
        action: ['TAXES'],
        status: ['APPROVED'],
        isTest: false,
        orgIds: (this.filter.orgIds && this.filter.orgIds.length) ? this.filter.orgIds : null,
        businessUnit: (this.filter.buIds && this.filter.buIds.length) ? this.filter.buIds : null,
      };
      if (this.dateRange && this.dateRange.from) {
        filters.registrationTime = {
          start: this.$moment(this.dateRange.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until || this.dateRange.from).endOf('day').toServerDateTimeFormat(),
        };
      }
      this.$refs.txModal.show(this.$t('monetaryDashboard.txf'), filters);
    },
    // Same EXACT data as the drill-down modal (one table, one type, exact
    // filters — so the numbers match the KPI), opened as a full page in a NEW
    // browser tab with totals + an editable filter bar.
    openKpiNewTab(title, extra) {
      const route = this.$router.resolve({
        name: 'filteredMonetaries',
        query: { title, f: JSON.stringify(this.kpiTxFilters(extra)) },
      });
      window.open(route.href, '_blank');
    },
    // ---- Leads-received number → assignment list (who got which lead/client) ----
    assignmentCtx() {
      return {
        range: { from: this.dateRange.from, until: this.dateRange.until },
        orgIds: this.filter.orgIds || [],
        buIds: this.filter.buIds || [],
      };
    },
    openAssignments(payload) {
      if (!payload || !payload.userId) return;
      const kind = payload.kind || 'SALES';
      const label = kind === 'RETENTION'
        ? this.$t('salesDashboard.assignments.clientsReceived')
        : this.$t('salesDashboard.assignments.leadsReceived');
      this.$refs.assignments.show(`${payload.name || ''} — ${label}`, {
        ...this.assignmentCtx(),
        userIds: [payload.userId],
        kind,
      });
    },
    // Header total: everything handed out in the period (whole team, no user filter).
    openTeamAssignments(kind = 'SALES') {
      const label = kind === 'RETENTION'
        ? this.$t('salesDashboard.assignments.clientsReceived')
        : this.$t('salesDashboard.assignments.leadsReceived');
      this.$refs.assignments.show(label, {
        ...this.assignmentCtx(),
        userIds: [],
        kind,
      });
    },
    openDrill(dimension, payload) {
      if (!payload || payload.value == null || payload.value === '') return;
      this.$refs.drill.open({
        dimension,
        value: payload.value,
        label: payload.label,
        ctx: this.drillCtx,
      });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.salesDeposits = data.salesDeposits || 0;
      this.statistics.retentionDeposits = data.retentionDeposits || 0;
      this.statistics.txfAmount = data.txfAmount || 0;
      this.statistics.txfCount = data.txfCount || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.onlineClients = data.clientsOnline || 0;
      this.statistics.ftdsCount = data.ftdsCount || 0;
      this.statistics.clientsCount = data.clientsCount || 0;
      this.statistics.duplicatedCount = data.duplicatedCount || 0;
      // FAKE fields: fall back to 0 — older cached responses may not have them.
      this.statistics.fakeDeposits = data.fakeDeposits || 0;
      this.statistics.fakeDepositsCount = data.fakeDepositsCount || 0;
      this.statistics.fakeWithdrawals = data.fakeWithdrawals || 0;
      this.statistics.fakeWithdrawalsCount = data.fakeWithdrawalsCount || 0;
      this.statistics.fakeNetDeposits = data.fakeNetDeposits || 0;
      this.statistics.userDeposit = data.userDeposit || {};
      this.statistics.userCount = data.userCount || {};
      this.statistics.agentBreakdown = data.agentBreakdown || [];
      this.statistics.salesAgentBreakdown = data.salesAgentBreakdown || [];
      this.statistics.countryDeposit = data.countryDeposit || {};
      this.statistics.countryCount = data.countryCount || {};
      this.statistics.statusMapCount = data.statusMapCount || {};
      this.statistics.clientCountryMapCount = data.clientCountryMapCount || {};
    },
    roleLabel(role) {
      if (role === 'SALE') return this.$t('salesDashboard.salesman');
      if (role === 'RETENTION') return this.$t('salesDashboard.retention');
      return '';
    },
    // Build a comma-separated CSV (with a UTF-8 BOM so Excel reads accents
    // correctly) from the given data and trigger a client-side download.
    downloadCsv(baseName, headers, rows) {
      const esc = v => {
        const s = v == null ? '' : String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [headers, ...rows].map(r => r.map(esc).join(','));
      const csv = '\uFEFF' + lines.join('\r\n'); // BOM so Excel detects UTF-8
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${baseName}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    // Sales + Retention export: one merged, styled XLSX built by the backend
    // (desk → agent → client rows), configured in a dedicated modal.
    openReportExport() {
      this.$refs.reportModal.open({
        range: { from: this.dateRange.from, until: this.dateRange.until },
        desks: this.filter.desks || [],
        orgIds: this.filter.orgIds || [],
        buIds: this.filter.buIds || [],
      });
    },
    exportDepositsByCountry() {
      const dep = this.statistics.countryDeposit || {};
      const cnt = this.statistics.countryCount || {};
      const codes = new Set([...Object.keys(dep), ...Object.keys(cnt)]);
      const rows = [...codes]
        .map(code => [
          this.countryMap[code] || code || 'Unknown',
          Math.round((Number(dep[code]) || 0) * 100) / 100,
          Number(cnt[code]) || 0,
        ])
        .sort((a, b) => b[1] - a[1]);
      this.downloadCsv('deposits-by-country', [
        this.$t('columns.country'), this.$t('salesDashboard.totalDeposited'), this.$t('salesDashboard.depositsCount'),
      ], rows);
    },
    exportClientsByStatus() {
      const rows = (this.statusItems || []).map(i => [i.label, i.value || 0]);
      this.downloadCsv('clients-by-status',
        [this.$t('salesDashboard.status'), this.$t('salesDashboard.clients')], rows);
    },
    exportClientsByCountry() {
      const rows = (this.clientCountryItems || []).map(i => [i.label, i.value || 0]);
      this.downloadCsv('clients-by-country',
        [this.$t('columns.country'), this.$t('salesDashboard.clients')], rows);
    },
  }
};
</script>

<style scoped lang="scss">
.sales-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__toolbar-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__export-btn {
    white-space: nowrap;
    box-shadow: 0 8px 18px -10px rgba(16, 185, 129, 0.7);
  }
}

/* One-click department shortcuts (fill the BU filter with a whole department) */
.dept-quick {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;

  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: all 0.18s ease;

    &--sales:hover { background: rgba(16, 185, 129, 0.1); color: #10b981; }
    &--retention:hover { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
    &--landing:hover { background: rgba(245, 158, 11, 0.12); color: #d97706; }

    &--sales.is-active {
      color: #fff;
      background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%);
      box-shadow: 0 6px 14px -6px rgba(16, 185, 129, 0.55);
    }
    &--retention.is-active {
      color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);
    }
    &--landing.is-active {
      color: #fff;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
      box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.55);
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
  &__pill.is-active .dept-quick__count { background: rgba(0, 0, 0, 0.16); }
}

.sales-dashboard--dark .dept-quick {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  .dept-quick__label { border-right-color: rgba(255, 255, 255, 0.08); }
  .dept-quick__pill { color: #b4b7bd; }
  .dept-quick__count { background: rgba(255, 255, 255, 0.1); }
}

.kpi-row {
  margin-top: 0.5rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-sep {
    opacity: 0.55;
    font-weight: 500;
    margin: 0 0.15rem;
  }

  /* FTD / Duplicated / Total: three labelled figures in one value row */
  &__value--triple {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .kpi-trip {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.05;
    min-width: 0;
  }
  .kpi-trip__num {
    font-size: 1.35rem;
    font-weight: 700;
  }
  .kpi-trip__label {
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.85;
    white-space: nowrap;
  }
  /* duplicated stays amber so it reads as a warning; clickable → drill to the clients */
  .kpi-trip--dup {
    cursor: pointer;
    &:focus { outline: none; }
    &:hover .kpi-trip__num { text-decoration: underline; }
  }
  .kpi-trip--dup .kpi-trip__num { color: #ffd27f; }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  /* Sales / Retention share chips inside the Total Deposits card */
  &__split {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-top: 0.35rem;
  }
  &__split-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.08rem 0.55rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.2);
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  /* drill-down affordance (same idea as the uim-kpi--click tiles) */
  &__expand {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  /* what-is-this info icon: top-left corner, tooltip explains the calculation */
  &__info {
    position: absolute;
    top: 10px;
    left: 12px;
    z-index: 4;
    font-size: 0.78rem;
    opacity: 0.55;
    cursor: help;
    transition: opacity 0.15s ease;
    &:hover { opacity: 1; }
  }

  /* open-in-new-tab affordance — sits just left of the expand icon */
  &__newtab {
    position: absolute;
    top: 8px;
    right: 32px;
    z-index: 4;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    font-size: 0.7rem;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.18s ease, background 0.18s ease, transform 0.18s ease;

    &:hover { background: rgba(255, 255, 255, 0.34); transform: translateY(-1px); }
    &:focus { outline: none; }
  }

  &--click {
    cursor: pointer;

    &:focus { outline: none; }
    &:hover .kpi-card__expand {
      opacity: 0.95;
      transform: scale(1.15);
    }
    &:hover .kpi-card__newtab { opacity: 0.95; }
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }

  &--txf {
    background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%);
  }

}

/* FAKE money strip: a deliberately small footnote row of amber mini-pills —
   nothing like the big real-money tiles, so fake money can't be misread.
   White-on-amber-gradient, so no dark-skin overrides are needed (same as the
   gradient KPI tiles above). */
.fake-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;

  /* The badge IS the on/off switch for everything fake on this page */
  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    border: 1px dashed rgba(255, 255, 255, 0.75);
    background: #ff9f43;
    color: #fff;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    line-height: 1.6;
    cursor: pointer;
    outline: none;
    transition: all 0.18s ease;

    &:focus { outline: none; }
    &:hover { filter: brightness(1.06); }

    &.is-off {
      background: transparent;
      border-color: rgba(255, 159, 67, 0.6);
      color: #e8590c;
      opacity: 0.8;

      .fake-strip__track { background: rgba(255, 159, 67, 0.3); }
      .fake-strip__thumb { transform: none; background: #ff9f43; }
    }
  }

  &__track {
    position: relative;
    width: 22px;
    height: 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.35);
    transition: background 0.18s ease;
  }
  &__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transform: translateX(10px);
    transition: transform 0.18s ease, background 0.18s ease;
  }
}

.fake-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ffb75d, #f0862b);
  color: #fff;
  font-size: 0.74rem;
  line-height: 1.2;
  box-shadow: 0 2px 6px rgba(240, 134, 43, 0.25);

  &__label { opacity: 0.92; font-weight: 600; }
  &__value { font-weight: 800; font-size: 0.8rem; }
  &__count { opacity: 0.85; font-size: 0.7rem; }

  &--click {
    cursor: pointer;
    &:hover { filter: brightness(1.06); }
    &:focus { outline: none; }
  }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22),
              0 1px 3px rgba(20, 24, 50, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px -18px rgba(20, 24, 50, 0.28),
                0 2px 4px rgba(20, 24, 50, 0.05);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  &__icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04));
    color: #007BFF;

    &--alt {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.04));
      color: #10b981;
    }
  }

  &__title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__toggle {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.95rem;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    flex: 1;
  }
}

.perf-row {
  margin-top: 0.25rem;
}

/* what-is-this info icon inside the perf chips' labels */
.perf-info {
  font-size: 0.72rem;
  opacity: 0.55;
  cursor: help;
  margin-left: 0.2rem;
  transition: opacity 0.15s ease;
  &:hover { opacity: 1; }
}

.perf-chip {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1.1rem;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 20px -14px rgba(20, 24, 50, 0.2), 0 1px 2px rgba(20, 24, 50, 0.04);
  height: 100%;

  &__icon {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    color: #fff;

    &--blue { background: linear-gradient(135deg, #4da3ff, #007BFF); }
    &--green { background: linear-gradient(135deg, #34d399, #10b981); }
    &--purple { background: linear-gradient(135deg, #a78bfa, #7c3aed); }
    &--amber { background: linear-gradient(135deg, #fbbf24, #f59e0b); }
  }
  &__value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2a44;
    line-height: 1.1;
  }
  &__label {
    font-size: 0.74rem;
    color: #6e6b7b;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-top: 0.15rem;
  }
}

.panel-card__hint {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  color: #b9b9c3;
  font-style: italic;
}
.panel-card__hint.sales-total {
  font-style: normal;
  font-size: 0.78rem;
  color: #6e6b7b;
  white-space: nowrap;
  strong { color: #1f2a44; font-weight: 700; }
  .sales-total__conv { color: #10b981; font-weight: 700; margin-left: 0.2rem; }
  .sales-total__received {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #2f7fd6, #4da3ff);
    color: #fff;
    font-weight: 800;
    white-space: nowrap;
    cursor: pointer;
    transition: filter 0.15s ease;
    &:hover, &:focus { filter: brightness(1.08); color: #fff; outline: none; }
  }
  .sales-total__ftd {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: rgba(0, 123, 255, 0.12);
    color: #007BFF;
    font-weight: 800;
  }
  /* FAKE pill: amber, like the fake treatment everywhere else — deliberately
     NOT red, so it can't be confused with the withdrawals pill */
  .sales-total__fake {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: rgba(255, 159, 67, 0.16);
    color: #e8590c;
    font-weight: 800;
    white-space: nowrap;
  }
  /* real money pills — same shape as the FAKE pill, green (deposits) / red (withdrawals) */
  .sales-total__real {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: rgba(40, 199, 111, 0.12);
    color: #1f9d57;
    font-weight: 800;
    white-space: nowrap;
  }
  .sales-total__wd {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: rgba(234, 84, 85, 0.1);
    color: #ea5455;
    font-weight: 800;
    white-space: nowrap;
  }
  /* money with no table row (reconciles the header with the KPI split) */
  .sales-total__other {
    display: inline-flex;
    align-items: center;
    padding: 0.1rem 0.55rem;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    color: #5e5873;
    font-weight: 800;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover, &:focus { background: rgba(110, 117, 159, 0.26); outline: none; }
  }
}

/* Managers/admins switch on the retention table (default OFF) */
.sd-mgr-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; flex-shrink: 0;
  margin-left: 0.5rem;
  border: 1px solid rgba(110, 117, 159, 0.24); border-radius: 999px;
  background: transparent; color: #6e6b7b; cursor: pointer; font-size: 0.8rem;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover { border-color: #00cfe8; color: #00cfe8; }
  &.is-on { background: #00cfe8; border-color: #00cfe8; color: #fff; }
}

/* Privacy eye — blurs the FTD counts and money in the agent tables */
.sd-eye {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; flex-shrink: 0;
  margin-left: 0.5rem;
  border: 1px solid rgba(110, 117, 159, 0.24); border-radius: 999px;
  background: transparent; color: #6e6b7b; cursor: pointer; font-size: 0.8rem;
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover { border-color: #7367f0; color: #7367f0; }
  &.is-on { background: #7367f0; border-color: #7367f0; color: #fff; }
}

/* Applied to every masked figure while the eye is on */
.sd-masked {
  filter: blur(6px);
  user-select: none;
  pointer-events: none;
}

.sales-dashboard--dark {
  .panel-card {
    background: #283046;
    box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6);
  }

  .perf-chip {
    background: #283046;
    box-shadow: 0 8px 20px -14px rgba(0, 0, 0, 0.55);
    .perf-chip__value { color: #d0d2d6; }
    .perf-chip__label { color: #8b93ab; }
  }

  .panel-card__hint { color: #8b93ab; }
  .panel-card__hint.sales-total {
    color: #8b93ab;
    strong { color: #d0d2d6; }
    .sales-total__ftd { background: rgba(77, 163, 255, 0.18); color: #8fc1ff; }
    .sales-total__fake { background: rgba(255, 159, 67, 0.2); color: #ffb054; }
    .sales-total__real { background: rgba(40, 199, 111, 0.16); color: #48da8c; }
    .sales-total__wd { background: rgba(234, 84, 85, 0.16); color: #ff8a8b; }
    .sales-total__other { background: rgba(255, 255, 255, 0.1); color: #b4b7bd; }
    .sales-total__received { background: linear-gradient(135deg, #2f7fd6, #4da3ff); color: #fff; }
  }

  .sd-eye {
    border-color: rgba(255, 255, 255, 0.16); color: #8b93ab;
    &:hover { border-color: #9e95f5; color: #9e95f5; }
    &.is-on { background: #7367f0; border-color: #7367f0; color: #fff; }
  }

  .sd-mgr-toggle {
    border-color: rgba(255, 255, 255, 0.16); color: #8b93ab;
    &:hover { border-color: #29e0f5; color: #29e0f5; }
    &.is-on { background: #00cfe8; border-color: #00cfe8; color: #fff; }
  }

  .panel-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .panel-card__title {
    color: #d0d2d6;
  }

  .panel-card__icon-chip {
    background: linear-gradient(135deg, rgba(77, 163, 255, 0.25), rgba(77, 163, 255, 0.05));
    color: #a8b6ff;

    &--alt {
      background: linear-gradient(135deg, rgba(46, 213, 162, 0.25), rgba(46, 213, 162, 0.05));
      color: #6bdcb1;
    }
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
